// Log de corridas. Autor, descargado por y tamaño salen «sin fuente»; la columna Estado se agrega
// al mock para que una corrida fallida no se lea como buena.
import { cn } from '@/lib/cn';
import { formatDateMx, formatTimeMx } from '@/lib/dateHelpers';
import { Skeleton } from '@/kit';
import { NoSource } from '@/shared/patterns/NoSource';
import { JOB_STATUS_CHIP, JOB_STATUS_LABEL } from '../logic';
import type { ReportRunEntry } from '../types';

const COLUMNS = [
    'Fecha',
    'Hora',
    'Reporte',
    'Formato',
    'Estado',
    'Autor corrida',
    'Descargado por',
    'Tamaño',
];

export function ReportRunsTable({
    entries,
    isPending,
}: {
    entries: ReportRunEntry[];
    isPending: boolean;
}) {
    return (
        <>
            <div className="overflow-x-auto">
                <table className="w-full min-w-[860px] border-collapse text-[11px]">
                    <thead>
                        <tr className="border-b border-border text-left text-[9.5px] font-bold uppercase tracking-[0.04em] text-muted-foreground">
                            {COLUMNS.map((column) => (
                                <th
                                    key={column}
                                    scope="col"
                                    className={cn(
                                        'px-2 py-1.5',
                                        column === 'Tamaño' && 'text-right',
                                    )}
                                >
                                    {column}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {isPending
                            ? Array.from({ length: 6 }, (_unused, row) => (
                                  <tr key={row}>
                                      {COLUMNS.map((column) => (
                                          <td key={column} className="px-2 py-1.5">
                                              <Skeleton className="h-3" />
                                          </td>
                                      ))}
                                  </tr>
                              ))
                            : entries.map((entry) => {
                                  const iso = new Date(entry.createdAt).toISOString();
                                  return (
                                      <tr key={entry.jobId} className="border-b border-border/50">
                                          <td className="px-2 py-1.5 tabular-nums">
                                              {formatDateMx(iso)}
                                          </td>
                                          <td className="px-2 py-1.5 tabular-nums">
                                              {formatTimeMx(iso)}
                                          </td>
                                          <td className="px-2 py-1.5">
                                              <div className="font-semibold">
                                                  {entry.reportName ?? (
                                                      <NoSource reason="Esta corrida es de una tabla del lake que ningún reporte del catálogo declara." />
                                                  )}
                                              </div>
                                              <div className="font-mono text-[9.5px] text-muted-foreground">
                                                  {entry.reportId ?? entry.table}
                                              </div>
                                          </td>
                                          <td className="px-2 py-1.5">
                                              <span
                                                  className="rounded border border-border px-1.5 py-px font-mono text-[9px] font-semibold"
                                                  title="El motor entrega el resultado en CSV; no genera PDF."
                                              >
                                                  CSV
                                              </span>
                                          </td>
                                          <td className="px-2 py-1.5">
                                              <span
                                                  className={cn(
                                                      'inline-flex rounded-full px-1.5 py-px text-[9px] font-bold',
                                                      JOB_STATUS_CHIP[entry.status],
                                                  )}
                                              >
                                                  {JOB_STATUS_LABEL[entry.status]}
                                              </span>
                                              {entry.failureReason ? (
                                                  <div className="mt-0.5 text-[9.5px] text-destructive">
                                                      {entry.failureReason}
                                                  </div>
                                              ) : null}
                                          </td>
                                          <td className="px-2 py-1.5">
                                              <NoSource reason="El historial de corridas no dice quién lanzó cada una." />
                                          </td>
                                          <td className="px-2 py-1.5">
                                              <NoSource reason="Nadie registra las descargas: el CSV se arma al pedirlo y no queda huella de quién lo bajó." />
                                          </td>
                                          <td className="px-2 py-1.5 text-right">
                                              <NoSource reason="El servicio de reportes no informa el tamaño del archivo." />
                                          </td>
                                      </tr>
                                  );
                              })}
                    </tbody>
                </table>
            </div>
            {!isPending && entries.length === 0 ? (
                <div className="py-8 text-center text-[12px] text-muted-foreground">
                    Todavía no hay corridas registradas.
                </div>
            ) : null}
        </>
    );
}
